import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

import accessLocalStorage from '../../services/accessLocalStorage';
import { exportSelectAllQueues } from '../../services/api';
import { getCsatSummary, getCsatResponses, getCsatRanking, downloadCsatCsv, downloadCsatRecording } from '../../services/csatApi';
import Loading from '../../components/Loading/LoadingModule';
import { CsatSummaryCards, CsatDistributionChart, CsatResponsesTable, CsatRankingTable } from '../../components/Csat/CsatDashboardParts';
import { lastSevenDays, resultErrorMessage } from '../../components/Config/Csat/csatScreen';

import '../Dashboard/styles.css';
import '../../components/Dashboard/FilterReportsCalls.css';
import '../../components/Dashboard/ReportList.css';
import './styles.css';

export const CSAT_DASHBOARD_MESSAGES = {
  title: 'Satisfação (CSAT)',
  subtitle: 'Notas, taxa de resposta e ranking do período',
  empty: 'Nenhuma pesquisa no período.',
  loadFailed: 'Não foi possível carregar os dados da pesquisa de satisfação.',
  apply: 'FILTRAR',
  allQueues: 'Todas as filas',
  exportCsv: 'Exportar CSV',
  rankingFailed: 'Não foi possível carregar o ranking.',
};

const PAGE_SIZE = 50;

const sessionTenant = () => {
  const session = accessLocalStorage.getUserLocalStorage();
  return session && session.user ? session.user.idtenant : null;
};

const sessionRole = () => {
  const session = accessLocalStorage.getUserLocalStorage();
  return session && session.user ? session.user.role : null;
};

const queueNamesOf = (response) => {
  if (!Array.isArray(response)) return [];
  return response.map((queue) => queue.namequeue).filter(Boolean);
};

function Csat() {
  const history = useHistory();
  const [filters, setFilters] = useState(() => ({ ...lastSevenDays(), queue: '', agent: '' }));
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [queues, setQueues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ranking, setRanking] = useState({ agent: [], queue: [] });
  const [notice, setNotice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const canDownloadRecording = sessionRole() !== 'user';

  useEffect(() => {
    if (!accessLocalStorage.getUserLocalStorage()) {
      history.push('/');
      return;
    }
    Promise.resolve(exportSelectAllQueues()).then((response) => setQueues(queueNamesOf(response))).catch(() => setQueues([]));
  }, [history]);

  const load = useCallback(async (current, currentPage) => {
    setLoading(true);
    setError(null);
    const params = { ...current, tenant: sessionTenant() };
    const [summaryResult, responsesResult, agentRanking, queueRanking] = await Promise.all([
      getCsatSummary(params),
      getCsatResponses({ ...params, page: currentPage, pageSize: PAGE_SIZE }),
      getCsatRanking({ ...params, groupBy: 'agent' }),
      getCsatRanking({ ...params, groupBy: 'queue' }),
    ]);
    setRanking({
      agent: agentRanking.success && agentRanking.data ? agentRanking.data.items : [],
      queue: queueRanking.success && queueRanking.data ? queueRanking.data.items : [],
    });
    if (!agentRanking.success || !queueRanking.success) setNotice(CSAT_DASHBOARD_MESSAGES.rankingFailed);
    else setNotice(null);
    if (!summaryResult.success || !responsesResult.success) {
      const failed = summaryResult.success ? responsesResult : summaryResult;
      setError(resultErrorMessage(failed, CSAT_DASHBOARD_MESSAGES.loadFailed));
      setSummary(null);
      setResponses({ items: [], total: 0 });
    } else {
      setSummary(summaryResult.data);
      setResponses(responsesResult.data || { items: [], total: 0 });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied, page);
  }, [applied, page, load]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    const result = await downloadCsatCsv({ ...applied, tenant: sessionTenant() });
    setExporting(false);
    if (!result.success) setNotice(result.error);
  };

  const downloadRecording = async (protocol) => {
    setNotice(null);
    const result = await downloadCsatRecording(protocol);
    if (!result.success) setNotice(result.error);
  };

  const change = (field) => (event) => setFilters((prev) => ({ ...prev, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  };

  const totalPages = Math.max(1, Math.ceil((responses.total || 0) / PAGE_SIZE));
  const empty = summary && summary.started === 0;
  const scalesWithAnswers = summary ? ['5', '10'].filter((scale) => summary.byScale[scale].answered > 0) : [];

  return (
    <div className="dashboard-container">
      {loading ? (
        <Loading />
      ) : (
        <div className="dashboard-content">
          <div className="dashboard-header">
            <div className="dashboard-title-wrapper">
              <svg className="dashboard-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <div>
                <h1 className="dashboard-title">{CSAT_DASHBOARD_MESSAGES.title}</h1>
                <p className="dashboard-subtitle">{CSAT_DASHBOARD_MESSAGES.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="filter-section">
            <div className="filter-header">
              <svg className="dashboard-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h1 className="filter-title">Filtro</h1>
            </div>
            <div className="filter-card">
              <form className="filter-form" onSubmit={submit}>
                <div className="filter-row">
                  <div className="filter-field">
                    <label className="filter-label" htmlFor="csat-from">De</label>
                    <input id="csat-from" className="filter-input" type="date" value={filters.from} onChange={change('from')} />
                  </div>
                  <div className="filter-field">
                    <label className="filter-label" htmlFor="csat-to">Até</label>
                    <input id="csat-to" className="filter-input" type="date" value={filters.to} onChange={change('to')} />
                  </div>
                  <div className="filter-field">
                    <label className="filter-label" htmlFor="csat-queue">Fila</label>
                    <select id="csat-queue" className="filter-select" value={filters.queue} onChange={change('queue')}>
                      <option value="">{CSAT_DASHBOARD_MESSAGES.allQueues}</option>
                      {queues.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </div>
                  <div className="filter-field">
                    <label className="filter-label" htmlFor="csat-agent">Ramal do atendente</label>
                    <input id="csat-agent" className="filter-input" inputMode="numeric" maxLength={8} value={filters.agent} onChange={change('agent')} />
                  </div>
                </div>
                <div className="filter-actions">
                  <button type="submit" className="filter-button">
                    <FontAwesomeIcon icon={faFilter} />
                    <span>{CSAT_DASHBOARD_MESSAGES.apply}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          {error && (
            <div className="section-card">
              <div className="dashboard-notice dashboard-notice-warning">
                <p className="dashboard-notice-message" role="alert">{error}</p>
              </div>
            </div>
          )}
          {notice && (
            <div className="section-card" role="status">
              <div className="dashboard-notice dashboard-notice-warning">
                <p className="dashboard-notice-message">{notice}</p>
              </div>
            </div>
          )}

          {!error && empty && (
            <div className="section-card">
              <div className="card-content-custom">
                <p className="csat-empty">{CSAT_DASHBOARD_MESSAGES.empty}</p>
              </div>
            </div>
          )}

          {!error && summary && !empty && (
            <div className="dashboard-section">
              <div className="section-card">
                <div className="card-header-custom">
                  <h2 className="card-title">Resumo</h2>
                </div>
                <div className="card-content-custom">
                  <CsatSummaryCards summary={summary} />
                </div>
              </div>

              {scalesWithAnswers.length > 0 && (
                <div className="section-card">
                  <div className="card-header-custom">
                    <h2 className="card-title">Distribuição das notas</h2>
                  </div>
                  <div className="card-content-custom">
                    <div className="charts-grid">
                      {scalesWithAnswers.map((scale) => (
                        <div className="column" key={scale}>
                          <CsatDistributionChart scale={scale} data={summary.byScale[scale]} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="charts-grid">
                <div className="column">
                  <CsatRankingTable title="Ranking por atendente" keyLabel="Ramal" items={ranking.agent} />
                </div>
                <div className="column">
                  <CsatRankingTable title="Ranking por fila" keyLabel="Fila" items={ranking.queue} />
                </div>
              </div>

              <div className="section-card">
                <div className="card-header-custom">
                  <h2 className="card-title">Respostas</h2>
                </div>
                <div className="card-content-custom">
                  <CsatResponsesTable items={responses.items} canDownloadRecording={canDownloadRecording} onDownloadRecording={downloadRecording} />
                  {totalPages > 1 && (
                    <div className="csat-page-nav">
                      <button type="button" className="filter-button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
                      <span>{`Página ${page} de ${totalPages}`}</span>
                      <button type="button" className="filter-button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="export-actions-card">
            <div className="export-action-item">
              <div className="export-info">
                <svg className="export-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="export-label">Exportar respostas do período</p>
              </div>
              <div className="export-buttons">
                <button type="button" className="button" onClick={exportCsv} disabled={exporting}>{CSAT_DASHBOARD_MESSAGES.exportCsv}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Csat;
